// What needs you, on My Team (B-211): a trade offer, claims waiting to run, a
// vote. One line each, and each opens the screen where it is dealt with.
//
// PRESENT ONLY WHEN SOMETHING IS. An empty list draws nothing, and so does
// one nobody has worked out (`null`): the roster is the screen's question,
// and "nothing pending" over it would be a sentence about a list the reader
// did not ask about. Home's panel said "Nothing pending. Enjoy the week."
// because the panel was always there; this strip is not.

import { whenInZone, type HomeView } from '@fsp/domain';
import { useNavigate } from 'react-router-dom';
import { useLeagueBase, useReaderZone } from '../../data/hooks';
import { useChatDrawer } from '../../shell/chatDrawer';
import { TeamMark } from '../../kit';
import { Button, Text } from '../../kit/ui';
import '../../styles/screens/home.css';
import { TeamName } from './Names';

type Item = NonNullable<HomeView['attention']>[number];

/** The items the strip shows: everything but the draft, which is Home's before a draft. */
export function attentionItems(attention: HomeView['attention']): Item[] {
  return (attention ?? []).filter((a) => a.kind !== 'draft');
}

export function AttentionStrip({ items, now }: { items: Item[]; now: string }) {
  const base = useLeagueBase();
  const navigate = useNavigate();
  const chat = useChatDrawer();
  const zone = useReaderZone();
  // A POLL THAT HAS CLOSED SINCE THE READ waits on nobody (B-579).
  const open = items.filter((a) => a.kind !== 'vote' || Date.parse(a.closesAt) > Date.now());
  if (open.length === 0) return null;
  return (
    <div className="fsp-attention" role="region" aria-label="Needs you">
      {open.map((a) =>
        a.kind === 'trade-offer' ? (
          <div key={a.tradeId} className="fsp-attention-row">
            <TeamMark team={a.fromTeam} size={24} />
            <div className="fsp-attention-body">
              <Text weight="strong">
                <TeamName team={a.fromTeam}>{a.fromTeam.ownerName}</TeamName> wants to trade
              </Text>
              <div className="fsp-sub fsp-ui-wrap-text">
                {a.summary} · {hoursLeft(a.expiresAt, now)}h left
              </div>
            </div>
            <Button
              size="xs"
              variant="primary"
              onClick={() => navigate(`${base}/trades?trade=${a.tradeId}`)}
            >
              Look
            </Button>
          </div>
        ) : a.kind === 'waiver-pending' ? (
          <div key="waivers" className="fsp-attention-row">
            <div className="fsp-attention-body">
              <Text weight="strong">
                {a.count} waiver claim{a.count === 1 ? '' : 's'} pending
              </Text>
              {/* The reader's zone, or no time until it is known (B-181). */}
              {zone === null ? null : (
                <div className="fsp-sub">
                  Processes {whenInZone(a.processesAt, zone, Date.now())}
                </div>
              )}
            </div>
            <Button size="xs" onClick={() => navigate(`${base}/waivers`)}>
              Waivers
            </Button>
          </div>
        ) : a.kind === 'vote' ? (
          <div key={a.pollId} className="fsp-attention-row">
            <div className="fsp-attention-body">
              <Text weight="strong">A league vote</Text>
              <div className="fsp-sub fsp-ui-wrap-text">{a.question}</div>
            </div>
            {/* A poll is a message in the league's chat (CHAT.md), its
                league channel and not the conversation last open. */}
            <Button
              size="xs"
              onClick={() => {
                chat.setView('league');
                chat.setOpen(true);
              }}
            >
              Vote
            </Button>
          </div>
        ) : null,
      )}
    </div>
  );
}

function hoursLeft(iso: string, nowIso: string): number {
  return Math.max(0, Math.round((Date.parse(iso) - Date.parse(nowIso)) / 3600000));
}
